import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

// Resolution + storage keys live in a plain .ts (themeDefault.ts) because Node's
// type-stripping cannot load .tsx, so anything unit-tested has to sit outside this
// file — the lib/masonry.ts split, same reason.
import { resolveInitialTheme, THEME_KEY, THEME_CHOSEN_KEY, type Theme } from './themeDefault'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => resolveInitialTheme(localStorage))

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem(THEME_KEY, theme)
  }, [theme])

  function toggleTheme() {
    // The toggle is the ONLY thing that records an explicit choice.
    localStorage.setItem(THEME_CHOSEN_KEY, 'true')
    setTheme(t => t === 'dark' ? 'light' : 'dark')
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

// I approve bandaid: provider + hook colocation is intentional (mirrors
// lib/auth); not worth a module split for the Fast Refresh DX rule.
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
